import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ExportRequest } from '@components/dashboard/export-dialog/export.model';
import { map, Observable, tap } from 'rxjs';
import { downloadBlob, buildFileName } from '@util/download';
import { BASE_PATH, Configuration, FilterValue } from 'src/openapi-client';

/** Filter keys that identify the data set itself (fund and date), as opposed to narrowing filters. */
const DATA_SET_FILTER_KEYS = ['funds', 'date', 'quick'];

/**
 * Exports the report behind a tile as a file. The file is generated by BusinessObjects in the
 * backend (`POST /export/tile/{id}?format=...`) with the same filter values the tile is displayed with.
 *
 * The endpoint is called with HttpClient directly instead of the generated `ExportService`
 * client: for a file response the generated client would pick `responseType: 'text'` (it does so
 * for every `text/*` type), and a download needs the response headers and the raw bytes.
 */
@Injectable({
  providedIn: 'root',
})
export class ExportService {
  private readonly http = inject(HttpClient);
  private readonly configuration = inject(Configuration, { optional: true });
  private readonly basePathToken = inject(BASE_PATH, { optional: true });

  /** Lets the backend generate the file and saves it via the browser's download. */
  export(request: ExportRequest): Observable<void> {
    return this.post(request).pipe(
      tap(response => this.save(response, request)),
      map(() => undefined)
    );
  }

  private post(request: ExportRequest): Observable<HttpResponse<Blob>> {
    const { url, params } = this.exportEndpoint(request);
    return this.http.post(`${this.basePath()}${url}`, this.filterValuesFor(request), {
      params,
      observe: 'response',
      responseType: 'blob',
      withCredentials: this.configuration?.withCredentials,
    });
  }

  /**
   * A tile export goes to `/export/tile/{tileId}` (only the report of the tile). A dashboard export goes
   * to `/export/dashboard/{documentId}`: XLSX contains the whole document, CSV needs the report index
   * (BusinessObjects exports csv per report only). Document and report index are part of the tile id.
   */
  private exportEndpoint(request: ExportRequest): { url: string; params: Record<string, string> } {
    const format = request.format.toLowerCase();
    if (request.target !== 'dashboard') {
      return { url: `/export/tile/${encodeURIComponent(request.tileId)}`, params: { format } };
    }
    const [documentId, reportIndex] = request.tileId.split('|');
    return {
      url: `/export/dashboard/${encodeURIComponent(documentId)}`,
      params: format === 'csv' && reportIndex !== undefined ? { format, reportIndex } : { format },
    };
  }

  /**
   * "current" exports what is displayed (all filters), "all" exports the whole fund: only the
   * filters that select the data set (fund, date) are kept, narrowing filters are dropped.
   */
  private filterValuesFor(request: ExportRequest): FilterValue[] {
    const values = request.filterValues ?? [];
    return request.scope === 'current' ? values : values.filter(value => DATA_SET_FILTER_KEYS.includes(value.key));
  }

  private save(response: HttpResponse<Blob>, request: ExportRequest): void {
    if (!response.body) {
      throw new Error('Der Export hat keine Daten geliefert');
    }
    downloadBlob(response.body, buildFileName(request.fileNameParts ?? [], request.format.toLowerCase(), 'analyse'));
  }

  private basePath(): string {
    const fromConfiguration =
      typeof this.configuration?.basePath === 'string' ? this.configuration.basePath : undefined;
    return this.basePathToken ?? fromConfiguration ?? '';
  }
}
